import { useCallback, useMemo, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import {
  BaseEdge,
  BuiltInNode,
  EdgeLabelRenderer,
  useReactFlow,
  useStore,
  type Edge,
  type EdgeProps,
  type XYPosition,
} from '@xyflow/react';

import { ControlPoint, type ControlPointData } from './ControlPoint';
import { getPath, getControlPoints } from './path';
import { Algorithm, COLORS } from './constants';
import { ControlAnchor } from './ControlAnchor';
import {
  getPointsBasedOnNodePositions,
  getStepInitialPoints,
  OFFSET,
} from './path/step';

export interface EdgeLabelData {
  id: string;
  text: string;
  position: number;
}

const clampLabelPosition = (value: number): number =>
  Math.min(0.95, Math.max(0.05, value));

const getDistance = (
  a: { x: number; y: number },
  b: { x: number; y: number }
): number => Math.hypot(b.x - a.x, b.y - a.y);

const getPointAtRatio = (
  points: Array<{ x: number; y: number }>,
  ratio: number
): { x: number; y: number } => {
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1) return points[0];

  const totalLength = points
    .slice(1)
    .reduce((sum, point, index) => sum + getDistance(points[index], point), 0);
  const targetDistance = totalLength * clampLabelPosition(ratio);
  let walked = 0;

  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i];
    const end = points[i + 1];
    const segmentLength = getDistance(start, end);
    if (segmentLength === 0) continue;

    if (walked + segmentLength >= targetDistance) {
      const localRatio = (targetDistance - walked) / segmentLength;
      return {
        x: start.x + (end.x - start.x) * localRatio,
        y: start.y + (end.y - start.y) * localRatio,
      };
    }

    walked += segmentLength;
  }

  return points[points.length - 1];
};

const getRatioForPoint = (
  points: Array<{ x: number; y: number }>,
  point: { x: number; y: number }
): number => {
  if (points.length < 2) return 0.5;

  const totalLength = points
    .slice(1)
    .reduce((sum, current, index) => sum + getDistance(points[index], current), 0);
  if (totalLength === 0) return 0.5;

  let bestDistance = Number.POSITIVE_INFINITY;
  let bestWalked = 0;
  let walked = 0;

  for (let i = 0; i < points.length - 1; i += 1) {
    const start = points[i];
    const end = points[i + 1];
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const segmentLengthSquared = dx * dx + dy * dy;
    if (segmentLengthSquared === 0) continue;

    const rawT =
      ((point.x - start.x) * dx + (point.y - start.y) * dy) /
      segmentLengthSquared;
    const t = Math.min(1, Math.max(0, rawT));
    const projected = {
      x: start.x + dx * t,
      y: start.y + dy * t,
    };
    const distanceToProjection = getDistance(point, projected);

    if (distanceToProjection < bestDistance) {
      bestDistance = distanceToProjection;
      bestWalked = walked + Math.sqrt(segmentLengthSquared) * t;
    }

    walked += Math.sqrt(segmentLengthSquared);
  }

  return clampLabelPosition(bestWalked / totalLength);
};

const createLabelPathGeometry = (
  path: string | undefined,
  fallbackPoints: Array<{ x: number; y: number }>
) => {
  const fallback = {
    getPointAtRatio: (ratio: number) => getPointAtRatio(fallbackPoints, ratio),
    getRatioForPoint: (point: { x: number; y: number }) =>
      getRatioForPoint(fallbackPoints, point),
  };

  if (!path || typeof document === 'undefined') return fallback;

  try {
    const pathElement = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path'
    );
    pathElement.setAttribute('d', path);

    const totalLength = pathElement.getTotalLength();
    if (!Number.isFinite(totalLength) || totalLength <= 0) return fallback;

    const samplesCount = Math.max(24, Math.ceil(totalLength / 8));
    const stepLength = totalLength / samplesCount;
    const sampledPoints = Array.from({ length: samplesCount + 1 }, (_, index) => {
      const length = Math.min(totalLength, index * stepLength);
      const point = pathElement.getPointAtLength(length);
      return { x: point.x, y: point.y, length };
    });

    return {
      getPointAtRatio: (ratio: number) => {
        const point = pathElement.getPointAtLength(
          totalLength * clampLabelPosition(ratio)
        );
        return { x: point.x, y: point.y };
      },
      getRatioForPoint: (point: { x: number; y: number }) => {
        let bestDistance = Number.POSITIVE_INFINITY;
        let bestLength = totalLength * 0.5;

        for (let i = 1; i <= samplesCount; i += 1) {
          const previous = sampledPoints[i - 1];
          const current = sampledPoints[i];
          const dx = current.x - previous.x;
          const dy = current.y - previous.y;
          const segmentLengthSquared = dx * dx + dy * dy;

          if (segmentLengthSquared > 0) {
            const rawT =
              ((point.x - previous.x) * dx + (point.y - previous.y) * dy) /
              segmentLengthSquared;
            const t = Math.min(1, Math.max(0, rawT));
            const projected = {
              x: previous.x + dx * t,
              y: previous.y + dy * t,
            };
            const distanceToProjection = getDistance(point, projected);

            if (distanceToProjection < bestDistance) {
              bestDistance = distanceToProjection;
              bestLength = previous.length + (current.length - previous.length) * t;
            }
          }
        }

        return clampLabelPosition(bestLength / totalLength);
      },
    };
  } catch {
    return fallback;
  }
};

const useIdsForInactiveControlPoints = (points: ControlPointData[]) => {
  const ids = useRef<string[]>([]);

  if (ids.current.length === points.length) {
    return points.map((point, i) =>
      point.id ? point : { ...point, id: ids.current[i] }
    );
  } else {
    ids.current = [];

    return points.map((point, i) => {
      if (!point.id) {
        const id = window.crypto.randomUUID();
        ids.current[i] = id;
        return { ...point, id: id };
      } else {
        ids.current[i] = point.id;
        return point;
      }
    });
  }
};

export type EditableEdge = Edge<{
  algorithm?: Algorithm;
  points: ControlPointData[];
  edgeLabels?: EdgeLabelData[];
  /** Привязка линии к слою плана */
  layerId?: number;
  isReadOnly?: boolean;
  connectionId?: number;
  deferLabelDragCommit?: boolean;
  edgeLabelsPendingSave?: boolean;
}>;

export function EditableEdgeComponent({
  id,
  selected,
  source,
  sourceX,
  sourceY,
  sourcePosition,
  target,
  targetX,
  targetY,
  targetPosition,
  markerEnd,
  markerStart,
  style,
  data = { points: [] },
  ...delegated
}: EdgeProps<EditableEdge>) {
  const sourceOrigin: XYPosition = { x: sourceX, y: sourceY };
  const targetOrigin: XYPosition = { x: targetX, y: targetY };
  const fallbackColor = COLORS[data.algorithm ?? Algorithm.BezierCatmullRom];
  const color = typeof style?.stroke === 'string' ? style.stroke : fallbackColor;
  const strokeWidth =
    typeof style?.strokeWidth === 'number'
      ? style.strokeWidth
      : typeof style?.strokeWidth === 'string'
        ? Number(style.strokeWidth) || 2
        : 2;

  const { screenToFlowPosition, setEdges } = useReactFlow<BuiltInNode, EditableEdge>();

  const shouldShowPoints = useStore((store) => {
    if (data.isReadOnly) return false;
    const sourceNode = store.nodeLookup.get(source)!;
    const targetNode = store.nodeLookup.get(target)!;

    return selected || sourceNode?.selected || targetNode?.selected;
  });

  const initialStepPoints = useMemo(
    () =>
      getStepInitialPoints({
        source: { x: sourceX, y: sourceY },
        target: { x: targetX, y: targetY },
        offset: OFFSET,
        sourcePosition,
        targetPosition,
      }).map((point, index) => ({ ...point, id: `${index}` })),
    [sourcePosition, sourceX, sourceY, targetPosition, targetX, targetY]
  );

  const isStepAlgorithm = data.algorithm === Algorithm.Step;

  /**
   * We calculate the modified points based on the node's movement using getPointsBasedOnNodePositions in case
   * of a step edge.
   */
  const updatedPointsBasedOnNodeMovement = useMemo(() => {
    if (!isStepAlgorithm) return data.points;
    return getPointsBasedOnNodePositions({
      points: data.points,
      source: { x: sourceX, y: sourceY },
      target: { x: targetX, y: targetY },
      sides: { fromSide: sourcePosition, toSide: targetPosition },
    });
  }, [
    isStepAlgorithm,
    data.points,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  ]);

  /**
   * We assign these modified points to a ref and then use that ref inside setControlPoints instead of directly using updatedPointsBasedOnNodeMovement
   * to avoid unnecessary reinitializations of setControlPoints + other places where it is being used.
   * */
  const updatedPointsRef = useRef<ControlPointData[]>([]);
  updatedPointsRef.current = updatedPointsBasedOnNodeMovement;

  const setControlPoints = useCallback(
    (update: (points: ControlPointData[]) => ControlPointData[]) => {
      setEdges((edges) =>
        edges.map((e) => {
          if (e.id !== id) return e;
          if (!isEditableEdge(e)) return e;

          let points: ControlPointData[] = [];
          if (isStepAlgorithm) points = updatedPointsRef.current;
          else points = e.data?.points ?? [];

          const updatedData = { ...e?.data, points: update(points) };

          return { ...e, data: updatedData };
        })
      );
    },
    [setEdges, id, isStepAlgorithm]
  );

  const pathPoints = [
    sourceOrigin,
    ...updatedPointsBasedOnNodeMovement,
    targetOrigin,
  ];
  const controlPoints = getControlPoints({
    points: pathPoints,
    algorithm: data.algorithm,
    sides: {
      fromSide: sourcePosition,
      toSide: targetPosition,
    },
    initialStepPoints,
  });
  const path = getPath({
    points: pathPoints,
    algorithm: data.algorithm,
    sides: {
      fromSide: sourcePosition,
      toSide: targetPosition,
    },
    initialStepPoints,
  });

  const labelPathGeometry = useMemo(
    () => createLabelPathGeometry(path, pathPoints),
    [path]
  );

  const controlPointsWithIds = useIdsForInactiveControlPoints(controlPoints);
  const edgeLabels = data.edgeLabels ?? [];

  const setEdgeLabels = useCallback(
    (update: (labels: EdgeLabelData[]) => EdgeLabelData[]) => {
      setEdges((edges) =>
        edges.map((e) => {
          if (e.id !== id) return e;
          if (!isEditableEdge(e)) return e;

          const labels = e.data?.edgeLabels ?? [];
          return {
            ...e,
            data: {
              ...e.data,
              points: e.data?.points ?? [],
              edgeLabelsPendingSave: e.data?.deferLabelDragCommit
                ? true
                : e.data?.edgeLabelsPendingSave,
              edgeLabels: update(labels),
            },
          };
        })
      );
    },
    [id, setEdges]
  );

  const handleLabelPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>, labelId: string) => {
      if (data.isReadOnly) return;
      event.preventDefault();
      event.stopPropagation();

      let pendingClientPoint: { x: number; y: number } | null = null;
      let animationFrame: number | null = null;
      const labelElement = event.currentTarget;
      const shouldDeferCommit = data.deferLabelDragCommit;

      const commitLabelPosition = (position: number) => {
        setEdgeLabels((labels) =>
          labels.map((label) =>
            label.id === labelId ? { ...label, position } : label
          )
        );
      };

      const flushLabelPosition = (commit = false) => {
        animationFrame = null;
        if (!pendingClientPoint) return;

        const flowPoint = screenToFlowPosition({
          x: pendingClientPoint.x,
          y: pendingClientPoint.y,
        });
        const position = labelPathGeometry.getRatioForPoint(flowPoint);
        if (shouldDeferCommit && !commit) {
          const labelPoint = labelPathGeometry.getPointAtRatio(position);
          labelElement.style.transform = `translate(-50%, -50%) translate(${labelPoint.x}px, ${labelPoint.y}px)`;
          return;
        }

        commitLabelPosition(position);
      };

      const handlePointerMove = (moveEvent: PointerEvent) => {
        pendingClientPoint = {
          x: moveEvent.clientX,
          y: moveEvent.clientY,
        };
        if (animationFrame == null) {
          animationFrame = window.requestAnimationFrame(() => flushLabelPosition());
        }
      };
      const handlePointerUp = () => {
        if (animationFrame != null) {
          window.cancelAnimationFrame(animationFrame);
          flushLabelPosition(true);
        } else if (pendingClientPoint) {
          flushLabelPosition(true);
        }
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
      };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp);
    },
    [data.deferLabelDragCommit, labelPathGeometry, screenToFlowPosition, setEdgeLabels]
  );

  return (
    <g
      role="button"
      aria-label={`Связь от ${source} к ${target}`}
      data-edge-id={id}
      tabIndex={0}
      focusable="true"
    >
      <BaseEdge
        id={id}
        path={path}
        {...delegated}
        markerStart={markerStart}
        markerEnd={markerEnd}
        style={{
          ...style,
          strokeWidth,
          stroke: color,
        }}
      />

      {shouldShowPoints &&
        !isStepAlgorithm &&
        controlPointsWithIds.map((point, index) => (
          <ControlPoint
            key={point.id}
            index={index}
            setControlPoints={setControlPoints}
            color={color}
            {...point}
          />
        ))}

      {/* Step Edge requires different logic for control point addition/movement/deletion so have made a seperate component for this */}
      {shouldShowPoints &&
        isStepAlgorithm &&
        controlPointsWithIds.map((point, index) => (
          <ControlAnchor
            key={point.id}
            index={index}
            setControlPoints={setControlPoints}
            color={color}
            direction={point.direction}
            initialStepPoints={
              initialStepPoints as unknown as ControlPointData[]
            }
            {...point}
          />
        ))}

      {edgeLabels.length > 0 && (
        <EdgeLabelRenderer>
          {edgeLabels
            .filter((label) => label.text.trim().length > 0)
            .map((label) => {
              const labelPoint = labelPathGeometry.getPointAtRatio(label.position ?? 0.5);
              return (
                <div
                  key={label.id}
                  className="nodrag nopan"
                  onPointerDown={(event) => handleLabelPointerDown(event, label.id)}
                  style={{
                    position: 'absolute',
                    transform: `translate(-50%, -50%) translate(${labelPoint.x}px, ${labelPoint.y}px)`,
                    pointerEvents: 'all',
                    cursor: 'grab',
                    zIndex: 1001,
                  }}
                  title="Перетащите метку вдоль линии"
                >
                  <div className="rounded border border-gray-300 bg-white/95 px-1.5 py-0.5 text-xs font-medium text-gray-900 shadow-sm">
                    {label.text}
                  </div>
                </div>
              );
            })}
        </EdgeLabelRenderer>
      )}
    </g>
  );
}

const isEditableEdge = (edge: Edge): edge is EditableEdge =>
  edge.type === 'editable-edge';
